import CryptoJS from 'crypto-js';

/**
 * Centralized encryption/decryption and checksum validation utility
 * This file provides common functions for API requests with optional encryption and checksum validation
 */

// ==================== Encryption/Decryption Functions ====================

/**
 * Encrypt data using AES encryption (same logic as Encryption-Decrypytion.jsx)
 * @param {Object|String} data - Data to encrypt (object will be stringified)
 * @returns {String} Encrypted and URL-encoded string
 */
export const encryptData = (data) => {
  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
    const key = CryptoJS.enc.Utf8.parse('MQ8wDQYDVQQHDAZN');
    const iv = CryptoJS.enc.Utf8.parse('MQ8wDQYDVQQHDAZN');
    const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(jsonString), key, {
      keySize: 128 / 8,
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    return encodeURIComponent(encrypted);
  } catch (error) {
    console.error('Error encrypting data:', error);
    throw error;
  }
};

/**
 * Decrypt data using AES decryption (same logic as Encryption-Decrypytion.jsx)
 * @param {String} encryptedData - Encrypted and URL-encoded string
 * @returns {Object} Decrypted JSON object
 */
export const decryptData = (encryptedData) => {
  try {
    const dataValue = decodeURIComponent(encryptedData);
    const key = CryptoJS.enc.Utf8.parse('MQ8wDQYDVQQHDAZN');
    const iv = CryptoJS.enc.Utf8.parse('MQ8wDQYDVQQHDAZN');
    
    const decrypted = CryptoJS.AES.decrypt(dataValue, key, {
      keySize: 128 / 8,
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    const jsonString = JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
    return jsonString;
  } catch (error) {
    console.error('Error decrypting data:', error);
    throw error;
  }
};

// ==================== Checksum Key Management ====================

/**
 * Get checksum key from runtime (localStorage) or environment or use default
 */
const getChecksumKeyP = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const runtimeKey = window.localStorage.getItem('CHECKSUM_KEY');
      if (runtimeKey && runtimeKey.trim().length > 0) {
        return runtimeKey;
      }
    }
  } catch (e) {
    // ignore and fall back
  }
  return process.env.REACT_APP_CHECKSUM_KEY || 'default-checksum-key-change-in-production';
};

/**
 * Generate checksum for request data
 * @param {Object|String} data - Request data (object will be stringified)
 * @returns {String} Checksum string
 */
export const generateChecksumPE = (data) => {
  try {
    const key = getChecksumKeyP();
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(dataString, key);
    return hmac.toString(CryptoJS.enc.Hex);
  } catch (error) {
    console.error('Error generating checksum:', error);
    return null;
  }
};

/**
 * Generate checksum with an explicit key (e.g., request-scoped guId)
 * @param {Object|String} data - Request data
 * @param {String} key - HMAC key to use
 * @returns {String} Checksum string
 */
export const generateChecksumWithKeyNew = (data, key) => {
  try {
    if (!key) {
      console.warn('generateChecksumWithKey called without key');
      return null;
    }
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(dataString, key);
    return hmac.toString(CryptoJS.enc.Hex);
  } catch (error) {
    console.error('Error generating checksum with key:', error);
    return null;
  }
};

/**
 * Validate checksum for response data
 * @param {Object|String} data - Response data (object will be stringified)
 * @param {String} receivedChecksum - Checksum received from server
 * @returns {Boolean} True if checksum is valid
 */
export const validateChecksum = (data, receivedChecksum) => {
  try {
    if (!receivedChecksum) {
      console.warn('No checksum received from server');
      return false;
    }
    
    const key = getChecksumKeyP();
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    const calculatedChecksum = CryptoJS.HmacSHA256(dataString, key).toString(CryptoJS.enc.Hex);
    
    return calculatedChecksum === receivedChecksum;
  } catch (error) {
    console.error('Error validating checksum:', error);
    return false;
  }
};

/**
 * Validate checksum using an explicit key (e.g., response guId)
 * @param {Object|String} data - Response data or raw text
 * @param {String} receivedChecksum - Checksum from headers
 * @param {String} key - HMAC key to use
 * @returns {Boolean}
 */
export const validateChecksumWithKey = (data, receivedChecksum, key) => {
  try {
    if (!receivedChecksum || !key) {
      return false;
    }
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    const calculatedChecksum = CryptoJS.HmacSHA256(dataString, key).toString(CryptoJS.enc.Hex);
    return calculatedChecksum === receivedChecksum;
  } catch (error) {
    console.error('Error validating checksum with key:', error);
    return false;
  }
};

/**
 * Generate checksum for encrypted request (when using encryption)
 * @param {String} encryptedData - Already encrypted request string
 * @returns {String} Checksum string
 */
export const generateChecksumForEncrypted = (encryptedData) => {
  try {
    const key = getChecksumKeyP();
    const hmac = CryptoJS.HmacSHA256(encryptedData, key);
    return hmac.toString(CryptoJS.enc.Hex);
  } catch (error) {
    console.error('Error generating checksum for encrypted data:', error);
    return null;
  }
};

// ==================== Main Fetch Wrapper ====================

/**
 * Global configuration for encryption (can be overridden per API call)
 * 
 * TO DISABLE ENCRYPTION GLOBALLY: Change GLOBAL_ENCRYPTION_ENABLED to false below
 * TO ENABLE CHECKSUM GLOBALLY: Change GLOBAL_CHECKSUM_ENABLED to true below
 * 
 * Or use setGlobalEncryption(false) and setGlobalChecksum(true) in your code
 */
let GLOBAL_ENCRYPTION_ENABLED = false;  // Set to true to enable encryption globally, false to disable
let GLOBAL_CHECKSUM_ENABLED = false;     // Set to true to enable checksum globally, false to disable

/**
 * Set global encryption flag
 * @param {Boolean} enabled - Whether to enable encryption globally
 */
export const setGlobalEncryption = (enabled) => {
  GLOBAL_ENCRYPTION_ENABLED = enabled;
};

/**
 * Set global checksum flag
 * @param {Boolean} enabled - Whether to enable checksum globally
 */
export const setGlobalChecksum = (enabled) => {
  GLOBAL_CHECKSUM_ENABLED = enabled;
};

/**
 * Comprehensive fetch wrapper with optional encryption/decryption and checksum validation
 * 
 * @param {String} url - API endpoint URL
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @param {Object} config - Configuration object
 * @param {Boolean} config.useEncryption - Enable request encryption and response decryption (default: uses GLOBAL_ENCRYPTION_ENABLED)
 * @param {Boolean} config.useDecryptResponse - Force decrypt response even if useEncryption is false (default: same as useEncryption)
 * @param {Boolean} config.useChecksum - Enable checksum generation for request and validation for request/response (default: uses GLOBAL_CHECKSUM_ENABLED)
 * @param {Boolean} config.validateResponseChecksum - Enable response checksum validation (default: same as useChecksum, can be set independently)
 * @param {String} config.checksumKey - Optional guId or custom key for checksum (if not provided, uses default key)
 * @param {Boolean} config.checksumUseKey - Use provided checksumKey instead of default key (default: false)
 * @returns {Promise<Object>} Promise that resolves to decrypted/parsed response object
 * 
 * @example
 * // Simple request with encryption and checksum
 * const response = await fetchApi('/Login', {
 *   method: 'POST',
 *   headers: { 'Authorization': 'Basic ...' },
 *   body: { userId: 'user', password: 'pass' }
 * }, {
 *   useEncryption: true,
 *   useChecksum: true
 * });
 * 
 * @example
 * // Disable encryption for specific API call
 * const response = await fetchApi('/SomeEndpoint', {
 *   method: 'POST',
 *   body: requestData
 * }, {
 *   useEncryption: false,
 *   useChecksum: false
 * });
 * 
 * @example
 * // Request with guId-based checksum
 * const response = await fetchApi('/Login', {
 *   method: 'POST',
 *   headers: { 'Authorization': 'Basic ...' },
 *   body: { userId: 'user', password: 'pass', guId: 'some-guid' }
 * }, {
 *   useEncryption: true,
 *   useChecksum: true,
 *   checksumKey: 'some-guid',
 *   checksumUseKey: true
 * });
 */
export const fetchApi = async (url, options = {}, config = {}) => {
  // Extract guId from headers automatically
  const guIdFromHeaders = (options.headers && (options.headers.guId || options.headers.GuId)) || null;
  
  // Set defaults: useEncryption: false, useChecksum: true, checksumUseKey: true (if guId available)
  // If guId is in headers, automatically enable checksum with key-based validation
  const {
    useEncryption = GLOBAL_ENCRYPTION_ENABLED,
    useChecksum = guIdFromHeaders ? true : (GLOBAL_CHECKSUM_ENABLED),
    validateResponseChecksum = undefined, // If undefined, defaults to useChecksum value
    checksumKey = guIdFromHeaders || null,
    checksumUseKey = guIdFromHeaders ? true : false,
   // returnRawFetchResponse = false,  // If true, return raw fetch Response object for manual processing
  } = config;
  
  // If validateResponseChecksum is not explicitly set, use useChecksum value
  const shouldValidateResponseChecksum = validateResponseChecksum !== undefined ? validateResponseChecksum : useChecksum;

  try {
    // Prepare request body
    let requestBody = options.body;
    let guIdFromRequest = null;

    // Extract guId from request body if present (before processing)
    if (requestBody && typeof requestBody === 'object' && requestBody.guId) {
      guIdFromRequest = requestBody.guId;
    }

    // Handle request body based on encryption setting
    if (useEncryption && requestBody && typeof requestBody === 'object') {
      // Encrypt the request body
      requestBody = encryptData(requestBody);
    } else if (requestBody && typeof requestBody === 'object') {
      // If no encryption, stringify the body
      requestBody = JSON.stringify(requestBody);
    }

    // Prepare headers (don't override if already set)
    const headers = { ...options.headers };
    
    // Generate and add checksum to request if enabled
    if (useChecksum && requestBody) {
      let checksum = null;
      let requestChecksumKey = null;
      
      if (checksumUseKey && checksumKey) {
        // Use provided key (e.g., guId from config)
        requestChecksumKey = checksumKey;
        checksum = generateChecksumWithKeyNew(requestBody, checksumKey);
       
      } else if (guIdFromRequest && checksumUseKey) {
        // Use guId from request body
        requestChecksumKey = guIdFromRequest;
        checksum = generateChecksumWithKeyNew(requestBody, guIdFromRequest);
       
      } else if (guIdFromHeaders && checksumUseKey) {
        // Use guId from headers (automatic detection)
        requestChecksumKey = guIdFromHeaders;
        checksum = generateChecksumWithKeyNew(requestBody, guIdFromHeaders);
       
      } else {
        // Use default checksum key
        requestChecksumKey = getChecksumKeyP();
        checksum = generateChecksumForEncrypted(requestBody);
       
      }
      
      if (checksum) {
        headers['checksum'] = checksum;
        console.log(' [REQUEST CHECKSUM] Generated checksum:', checksum);
        console.log(' [REQUEST CHECKSUM] Request body (first 100 chars):', 
          typeof requestBody === 'string' ? requestBody.substring(0, 100) : JSON.stringify(requestBody).substring(0, 100));
      } else {
        console.warn(' [REQUEST CHECKSUM] Failed to generate checksum');
      }
    }

    // Set Content-Type only if not already set
    if (!headers['Content-Type']) {
      if (useEncryption) {
        headers['Content-Type'] = 'text/plain';
      } else {
        headers['Content-Type'] = 'application/json';
      }
    }

    // Make the fetch call
    const response = await fetch(url, {
      ...options,
      method: options.method || 'GET',
      headers: headers,
      body: requestBody
    });

    // // If returnRawFetchResponse is true, return the raw Response object for manual processing
    // if (returnRawFetchResponse) {
    //   return response;
    // }

    // Get response as text first
    const responseText = await response.text();
    
    // Handle response decryption/parsing
    let responseData = null;
    if (useEncryption) {
      // Try to decrypt response
      try {
        responseData = decryptData(responseText);
      } catch (error) {
        // If decryption fails, response might not be encrypted
        console.warn('Decryption failed, trying JSON parse:', error);
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          responseData = responseText;
        }
      }
    } else {
      // Try to parse as JSON
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        // If JSON parsing fails, try decrypting (server might still send encrypted)
        try {
          responseData = decryptData(responseText);
        } catch (decryptError) {
          // If both fail, return as text
          responseData = responseText;
        }
      }
    }

    // Validate response checksum if enabled
    if (shouldValidateResponseChecksum && responseData) {
      const receivedChecksum = response.headers.get('checksum') || 
                               response.headers.get('Checksum') || 
                               response.headers.get('x-checksum') || 
                               response.headers.get('X-Checksum');
      
      console.log(' [RESPONSE CHECKSUM] Received checksum from header:', receivedChecksum);
      
      if (receivedChecksum) {
        // Convert response data to string for checksum validation
        const dataString = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
        
        // Extract guId from response for validation
        let responseGuId = null;
        if (responseData && typeof responseData === 'object' && responseData.guId) {
          responseGuId = responseData.guId;
          // Store guId in localStorage for future requests
          try {
            if (typeof window !== 'undefined' && window.localStorage) {
              window.localStorage.setItem('CHECKSUM_KEY', responseGuId);
            }
          } catch (e) {
            // ignore storage errors
          }
        }

        let isValid = false;
        let responseChecksumKey = null;
        let computedChecksum = null;
        
        if (checksumUseKey && (responseGuId || checksumKey)) {
          // Use guId from response or provided key
          const keyToUse = responseGuId || checksumKey || guIdFromHeaders;
          responseChecksumKey = keyToUse;
          computedChecksum = generateChecksumWithKeyNew(dataString, keyToUse);
          isValid = validateChecksumWithKey(dataString, receivedChecksum, keyToUse);
          
         
        } else {
          // Use default checksum key
          responseChecksumKey = getChecksumKeyP();
          computedChecksum = generateChecksumPE(dataString);
          isValid = validateChecksum(dataString, receivedChecksum);
          
          
        }

        console.log(' [RESPONSE CHECKSUM] Computed checksum:', computedChecksum);
        console.log(' [RESPONSE CHECKSUM] Received checksum:', receivedChecksum);
        console.log(' [RESPONSE CHECKSUM] Response data (first 200 chars):', dataString.substring(0, 200));
        
        if (isValid) {
          console.log(' [RESPONSE CHECKSUM] Validation PASSED - Checksums MATCH');
        } else {
          console.error(' [RESPONSE CHECKSUM] Validation FAILED - Checksums DO NOT MATCH');
          console.error('   Expected:', computedChecksum);
          console.error('   Received:', receivedChecksum);
          throw new Error('Checksum validation failed for response');
        }
      } else {
        console.warn(' [RESPONSE CHECKSUM] No checksum received in response headers');
      }
    }

    // Return response data with status information
    return {
      data: responseData,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      ok: response.ok
    };
  } catch (error) {
    console.error('Error in fetchApi:', error);
    throw error;
  }
};

/**
 * Legacy wrapper for fetch API calls with automatic checksum handling (without encryption)
 * @deprecated Use fetchApi instead with appropriate config
 * @param {String} url - API endpoint URL
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise} Fetch promise with checksum validation
 */
export const fetchWithChecksum = async (url, options = {}) => {
  // Add checksum to request if body exists
  if (options.body) {
    const checksum = generateChecksumForEncrypted(options.body);
    if (checksum) {
      options.headers = options.headers || {};
      options.headers['checksum'] = checksum;
    }
  }

  // Make the fetch call
  const response = await fetch(url, options);
  
  // Clone response for validation without consuming the original
  const responseText = await response.clone().text();
  
  // Validate response checksum if present
  const receivedChecksum = response.headers.get('checksum') || response.headers.get('x-checksum');
  if (receivedChecksum && responseText) {
    const isValid = validateChecksum(responseText, receivedChecksum);
    if (!isValid) {
      throw new Error('Checksum validation failed for response');
    }
  }

  // Return original response
  return response;
};
